import { configureStore, combineReducers, Action } from '@reduxjs/toolkit';
import accountSlice from '@/lib/stores/accountSlice';
import subscribeSlice from '@/lib/stores/subscribeSlice';
import dataSlice from '@/lib/stores/dataSlice';
import walletSlice from '@/lib/stores/walletSlice';
import stateSlice from '@/lib/stores/stateSlice';
import tradeSlice from '@/lib/stores/tradeSlice';
import uiReducer from './uiSlice';
import wsTokenReducer from './wsTokenSlice';
import wsSimpleToken from './wsSimpleTokenInfo';
import wsTradeListReducer from './wsTradeListSlice';
import wsUserDataReducer from './wsUserDataSlice';

// 定义重置 action
export const RESET_STATE = 'RESET_STATE';

// 合并所有 reducers
const appReducer = combineReducers({
  account: accountSlice,
  subscribe: subscribeSlice,
  data: dataSlice,
  state: stateSlice,
  wallet: walletSlice,
  trade: tradeSlice,
  ui: uiReducer,
  wsToken: wsTokenReducer,
  wsSimpleToken: wsSimpleToken,
  wsTradeList: wsTradeListReducer,
  wsUserData: wsUserDataReducer,
});

// 获取 appReducer 的返回类型
type AppState = ReturnType<typeof appReducer>;

// 创建根 reducer，处理重置逻辑
const rootReducer = (state: AppState | undefined, action: Action): AppState => {
  if (action.type === RESET_STATE) {
    // 保留 websocket 相关的状态
    const { state: wsState, subscribe, wsUserData } = state ?? {};
    return {
      ...appReducer(undefined, action),
      state: wsState,
      subscribe,
      wsUserData,
    } as AppState;
  }
  return appReducer(state, action);
};

export const makeStore = () => {
  return configureStore({
    reducer: rootReducer,
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware({
        serializableCheck: {
          ignoredActions: [
            'wallet/setProvider',
            'trade/setAmount',
            'stateSlice/setSendJsonMessage',
            RESET_STATE,
          ],
          ignoredPaths: [
            'wallet.provider',
            'trade.amount',
            'state.websocketState.sendJsonMessage',
          ],
        },
      }),
  });
};

// Infer the type of makeStore
export type AppStore = ReturnType<typeof makeStore>;
// Infer the `RootState` and `AppDispatch` types from the store itself
export type RootState = ReturnType<AppStore['getState']>;
export type AppDispatch = AppStore['dispatch'];

// 导出重置方法
export const resetStore = () => ({
  type: RESET_STATE,
});
